import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Plane, Recycle } from "lucide-react";
import { REGISTER_URL, LOGIN_URL, getPartners } from "@/lib/api";

const CTA_IMG =
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB0cmF2ZWwlMjBhYnN0cmFjdCUyMGJhY2tncm91bmQlMjBkYXJrfGVufDB8fHx8MTc4Mzc4Mjk0M3ww&ixlib=rb-4.1.0&q=70&w=1920&auto=format"; // Unsplash resizes to 1920px wide and serves AVIF/WebP (0.7 MB → ~20 KB)

const PARTNER_LOGOS = {
  IVAO: "/logos/ivao.svg",
  VATSIM: "/logos/vatsim.png",
  IndiaVACC: "/logos/indiavacc.svg",
};

export const Footer = () => {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    getPartners().then(setPartners).catch(() => {});
  }, []);

  return (
    <footer data-testid="footer-section">
      <div className="mx-auto max-w-7xl px-5 pb-16 lg:px-10">
        <div className="relative overflow-hidden rounded-3xl border border-white/10">
          {/* footer banner sits at the bottom of the page, so load it lazily and decode off the main thread */}
          <img src={CTA_IMG} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--aiv-bg-solid)] via-[var(--aiv-bg-solid)]/90 to-[#8A1538]/40" />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative px-8 py-16 lg:px-16 lg:py-24"
          >
            <p className="text-overline text-[var(--aiv-accent)]">Ready for departure?</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-black tracking-tight text-[var(--aiv-text)] lg:text-5xl">
              Log your first flight with Air India Virtual.
            </h2>
            <p className="mt-5 max-w-xl text-base text-[var(--aiv-text-2)]">
              Join over two thousand pilots. Free to fly, forever. Grab your
              callsign and take to the virtual skies today.
            </p>
            <Link
              to={REGISTER_URL}
              data-testid="cta-register-btn"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#8A1538] px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#A81A45]"
            >
              Become a Pilot
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-5 py-10 lg:flex-row lg:px-10">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#8A1538]">
              <Plane className="h-4 w-4 -rotate-45 text-[var(--aiv-accent)]" />
            </span>
            <div>
              <p className="font-display text-base font-bold text-[var(--aiv-text)]">Air India Virtual</p>
              <p className="text-xs text-[var(--aiv-text-2)]">Oldest Indian Virtual Airline since 2009</p>
            </div>
          </div>

          <div className="flex items-center gap-6 text-sm text-[var(--aiv-text-2)]">
            <a href="#about" className="transition-colors hover:text-[var(--aiv-text)]">About</a>
            <a href="#stats" className="transition-colors hover:text-[var(--aiv-text)]">Stats</a>
            <a href="#leaderboard" className="transition-colors hover:text-[var(--aiv-text)]">Leaderboard</a>
            <Link to={LOGIN_URL} className="transition-colors hover:text-[var(--aiv-text)]">Login</Link>
          </div>
        </div>
        {partners.length > 0 && (
          <div className="mx-auto max-w-7xl px-5 pb-8 lg:px-10">
            <div className="flex flex-col items-center gap-4 border-t border-white/5 pt-8 lg:flex-row lg:justify-between">
              <p className="text-overline text-[var(--aiv-text-2)]">Proud Network Partners</p>
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4" data-testid="footer-partners-list">
                {partners.map((p) => {
                  const logo = PARTNER_LOGOS[p.name];
                  return (
                    <a
                      key={p.name}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-partner-logo-${p.name.toLowerCase()}`}
                      aria-label={p.name}
                      title={p.name}
                      className="opacity-70 transition-opacity duration-200 hover:opacity-100"
                    >
                      {logo ? (
                        <img src={logo} alt={`${p.name} logo`} className="h-6 w-auto object-contain md:h-7" />
                      ) : (
                        <span className="text-sm font-semibold text-[var(--aiv-text)]">{p.name}</span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-2 px-5 pb-8 lg:flex-row lg:items-start lg:justify-between lg:px-10">
          <p className="text-center text-xs text-[var(--aiv-text-2)]/60 lg:text-left">
            Dedicated to Mr. J.R.D Tata. We are NOT affiliated in any manner with the real airline.
            This is a non-commercial flight-simulation community. © {new Date().getFullYear()} Air India Virtual.
          </p>
          <div className="flex flex-col items-center gap-2 lg:items-end">
            <p className="text-xs text-[var(--aiv-text-2)]/60">AI Virtual powered by</p>
            <div className="flex items-center gap-4">
              <a
                href="https://phpvms.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[var(--aiv-text-2)]/60 transition-colors hover:text-[var(--aiv-text)]"
              >
                <img src="/logos/phpvms.png" alt="" className="h-4 w-4 rounded object-contain" />
                phpVMS v7
              </a>
              <a
                href="https://github.com/FatihKoz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[var(--aiv-text-2)]/60 transition-colors hover:text-[var(--aiv-text)]"
              >
                <Recycle className="h-4 w-4" />
                Disposable
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
